/**
 * useHaptics Hook
 * Provides haptic feedback for timeline scrubbing interactions.
 *
 * Web: Uses the Vibration API (navigator.vibrate) — works on Android Chrome,
 * no-op on iOS Safari / desktop. Falls back gracefully.
 *
 * Future React Native: Swap import to react-native-haptic-feedback or
 * Expo Haptics for Core Haptics (iOS) / Vibrator (Android).
 *
 * Patterns:
 * - tick()      — light pulse as scrubber crosses a day boundary (10ms)
 * - snap()      — medium pulse when snapping to an event marker (25ms)
 * - boundary()  — double-tap when hitting start/end of timeline (15-30-15ms)
 * - select()    — confirm pulse when user lifts finger on a date (40ms)
 */

import { useRef, useCallback } from 'react';

// ============================================================================
// Types
// ============================================================================

interface HapticOptions {
  /** Disable all haptics (e.g. user preference, accessibility) */
  disabled?: boolean;
}

interface HapticActions {
  /** Light tick — scrubber crosses a date boundary */
  tick: () => void;
  /** Medium snap — scrubber locks onto an event marker */
  snap: () => void;
  /** Double-tap — scrubber hits timeline start or end */
  boundary: () => void;
  /** Confirm pulse — user lifts finger to select a date */
  select: () => void;
  /** Throttled tick that fires at most once per interval (for smooth scrubbing) */
  scrubTick: (currentDate: string) => void;
}

// ============================================================================
// Vibration patterns (milliseconds)
// ============================================================================

const PATTERNS = {
  tick: [10],
  snap: [25],
  boundary: [15, 30, 15],
  select: [40],
} as const;

/** Minimum interval between scrub ticks (ms) to avoid motor buzz */
const SCRUB_THROTTLE_MS = 60;

// ============================================================================
// Helpers
// ============================================================================

function canVibrate(): boolean {
  return typeof navigator !== 'undefined' && 'vibrate' in navigator;
}

function vibrate(pattern: readonly number[]): void {
  if (canVibrate()) {
    try {
      navigator.vibrate([...pattern]);
    } catch {
      // Silently fail — not all contexts allow vibration
    }
  }
}

// ============================================================================
// Hook
// ============================================================================

export function useHaptics(options: HapticOptions = {}): HapticActions {
  const { disabled = false } = options;
  const lastTickRef = useRef(0);
  const lastDateRef = useRef('');

  const tick = useCallback(() => {
    if (disabled) return;
    vibrate(PATTERNS.tick);
  }, [disabled]);

  const snap = useCallback(() => {
    if (disabled) return;
    vibrate(PATTERNS.snap);
  }, [disabled]);

  const boundary = useCallback(() => {
    if (disabled) return;
    vibrate(PATTERNS.boundary);
  }, [disabled]);

  const select = useCallback(() => {
    if (disabled) return;
    vibrate(PATTERNS.select);
  }, [disabled]);

  const scrubTick = useCallback(
    (currentDate: string) => {
      if (disabled) return;

      const now = performance.now();
      // Only fire if date changed AND enough time has passed
      if (
        currentDate !== lastDateRef.current &&
        now - lastTickRef.current >= SCRUB_THROTTLE_MS
      ) {
        lastDateRef.current = currentDate;
        lastTickRef.current = now;
        vibrate(PATTERNS.tick);
      }
    },
    [disabled]
  );

  return { tick, snap, boundary, select, scrubTick };
}

export default useHaptics;
